import { useContext, useMemo, useState } from 'react';
import { Metric, ProductPPG, PromoEvent } from 'types/tradePromoOptimization';
import { TPODataContext } from 'contexts/TPOContext';
import KPISelector, { KPIItem } from './KPISelector';
import {
    checkPromoStatus,
    getPromoMetricByNameKeyName,
    handleMetricInputChange,
    handleTotalInputChange,
    handleUnselectSpendMetrics,
    isValidMetricInput,
} from '../../tpoHelperMethods';
import { Tooltip } from 'antd';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';
import MetricCell from './MetricSection/MetricCell';
import { tpoPromoMetricKeys } from 'utils/Enums';
import TotalsCell from './MetricSection/TotalsCell';
import { getEditButtonState } from 'utils/helperMethods';

const SpendDetails = ({ promoSyncId, showTotals }: { promoSyncId: string; showTotals: boolean }) => {
    const {
        updateSelectedEventProperty,
        selectedEventInfo,
        viewMode,
        isCurrentPromoFormDisabled,
        isTpmTab,
        promoConfigDictionary,
        skipSRPUpdateRef,
        tempEventProperties,
        finalCalenderStatues,
    } = useContext(TPODataContext);
    const combinedViewMode = viewMode || isTpmTab || isCurrentPromoFormDisabled;
    const [isPopoverVisible, setIsPopoverVisible] = useState(false);

    const [editingCell, setEditingCell] = useState<{ metricId: number; ppgName: string } | null>(null);
    const [editValue, setEditValue] = useState('');
    const [warningMessage, setWarningMessage] = useState<string>('');

    const currentPromo = selectedEventInfo?.currentPromoTactic;
    const planStatus = finalCalenderStatues?.approval_status?.toLowerCase();
    const promoStatus = selectedEventInfo?.currentPromoTactic?.status?.toLowerCase();

    //ToDO move to utility function

    const isInputDisabled = (metricId: number, skipEditableCheck = false) => {
        const item =
            promoConfigDictionary.promoMetricMapping?.[Number(currentPromo?.promoType)]?.spendMetrics?.[metricId];

        const isPastDatesEditable = item?.is_past_dates_editable;
        const isEditable = item?.editable;
        const metricNameKey = item?.name_key;

        const isSpecialMetric =
            metricNameKey === tpoPromoMetricKeys.SRP_TRUE || metricNameKey === tpoPromoMetricKeys.PROMOTED_SRP;

        const getStatus = checkPromoStatus(promoStatus as string);
        if (getStatus) {
            return true;
        }
        if (isSpecialMetric) {
            return !isEditable || !isPastDatesEditable;
        }

        const editButtonDisabledState = getEditButtonState(
            planStatus,
            selectedEventInfo,
            tempEventProperties?.CRAStartDate,
        )?.isDisabled;

        // For list items, skip the editable check
        if (skipEditableCheck) {
            return editButtonDisabledState;
        }

        return !isEditable || editButtonDisabledState;
    };

    const spendDetailKPIs: KPIItem[] = useMemo(() => {
        const kpis =
            currentPromo?.ppgs[0]?.spendDetails.map((m: Metric) => ({
                value: m.id?.toString(),
                label: m.name,
                isSelected: m.isSelected,
                isHidden: m.isHidden,
            })) || [];

        const orderMap: Record<number, number> = Object.entries(promoConfigDictionary?.spendMetrics || {}).reduce(
            (acc, [key, value]) => {
                acc[value.id] = value.orderId ?? 0;
                return acc;
            },
            {} as Record<number, number>,
        );
        return kpis.sort((a, b) => {
            const orderA = orderMap[Number(a.value)] ?? 0;
            const orderB = orderMap[Number(b.value)] ?? 0;
            return orderA - orderB;
        });
    }, [currentPromo?.ppgs, promoConfigDictionary?.spendMetrics]);

    const metricKPIsForSelector: KPIItem[] = useMemo(() => {
        if (currentPromo?.promoType) {
            const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo.promoType]?.spendMetrics;
            const kpis = currentPromo?.ppgs[0]?.spendDetails.map((m: Metric) => ({
                value: m.id?.toString(),
                label: m.name,
                isSelected: m.isSelected,
                groupName: avlMetrics[m.id]?.groupName || '',
                isHidden: m.isHidden,
                isDisabled: isInputDisabled(m.id, true),
            }));

            const allGroups = Array.from(new Set(kpis.map((kpi) => kpi.groupName)));

            return allGroups.sort().map((groupName) => {
                const groupMetrics = kpis.filter((kpi) => kpi.groupName === groupName);
                const isGroupDisabled = groupMetrics.length > 0 && groupMetrics.every((kpi) => kpi.isDisabled);

                return {
                    value: groupName,
                    label: groupName,
                    isSelected: kpis.some((kpi) => kpi.groupName === groupName && kpi.isSelected),
                    isHidden: kpis.some((kpi) => kpi.groupName === groupName && kpi.isHidden),
                    isDisabled: isGroupDisabled,
                };
            });
        }
        return [];
    }, [spendDetailKPIs]);

    const handleInputChange = (metricId: number, productName: string, value: string) => {
        if (!selectedEventInfo?.currentPromoTactic) return;

        const updatedPPGs: any = handleMetricInputChange(
            promoConfigDictionary,
            selectedEventInfo,
            metricId,
            productName,
            value,
            false,
        );

        if (updatedPPGs)
            // Save changes
            updateSelectedEventProperty('currentPromoTactic', {
                ...selectedEventInfo?.currentPromoTactic,
                ppgs: updatedPPGs,
                hasChanges: true,
            });
    };

    const onMetricKPISelectHandler = (updatedGroups: string[]) => {
        // avoid setting srp value
        skipSRPUpdateRef.current = true;

        // Guard against undefined
        if (!selectedEventInfo?.currentPromoTactic) return;

        const currentSavedSpendIds = new Set(
            selectedEventInfo?.currentPromoTactic?.ppgs[0]?.spendDetails
                .filter((m: Metric) => m.isSelected)
                ?.map((m) => m.id),
        );

        // `updatedGroups` contains selected groupNames
        const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo?.promoType || 1]?.spendMetrics;
        selectedEventInfo?.currentPromoTactic.ppgs.forEach((ppg: ProductPPG) => {
            ppg.spendDetails.forEach((metric: Metric) => {
                metric.isSelected = updatedGroups.includes(avlMetrics[metric.id].groupName);
                if (metric.isSelected && currentSavedSpendIds.has(metric.id)) currentSavedSpendIds.delete(metric.id);
            });
        });

        if (selectedEventInfo?.currentPromoTactic.ppgs) {
            const newSelectedEventInfo = handleUnselectSpendMetrics(
                selectedEventInfo,
                Array.from(currentSavedSpendIds),
                promoConfigDictionary,
            );
            // enable save button on change of kpis
            if (!viewMode && newSelectedEventInfo) {
                newSelectedEventInfo.currentPromoTactic.hasChanges = true;
            }

            if (newSelectedEventInfo?.currentPromoTactic) {
                updateSelectedEventProperty('currentPromoTactic', _.cloneDeep(newSelectedEventInfo.currentPromoTactic));
            }
        }
    };

    return (
        <>
            <div className=" flex">
                <div className="flex flex-col min-w-[260px]  max-w-[260px] ">
                    {spendDetailKPIs
                        ?.filter((m: KPIItem) => m.isSelected && !m.isHidden)
                        ?.map((metric: KPIItem) => {
                            return (
                                <Tooltip
                                    placement="top"
                                    color="white"
                                    destroyTooltipOnHide
                                    overlayStyle={{ borderRadius: 4 }}
                                    overlayInnerStyle={{
                                        color: colorConstants.darkGrey,
                                    }}
                                    title={metric.label}
                                >
                                    <div
                                        className={`block truncate h-9 border-l border-r border-b p-2 font-xs border-backgroundGreyAlt1 bg-white
                                          ${isInputDisabled(Number(metric.value)) ? 'text-backgroundGreyAlt2' : ''}
                                        `}
                                    >
                                        {metric.label}
                                    </div>
                                </Tooltip>
                            );
                        })}
                </div>

                <div className={`flex flex-grow overflow-x-scroll  hide-scrollable ${promoSyncId} `}>
                    {showTotals && currentPromo?.ppgs && (
                        <TotalsCell
                            ppgs={currentPromo?.ppgs}
                            headerKPIs={spendDetailKPIs}
                            promoConfigDictionary={promoConfigDictionary}
                            isInputDisabled={isInputDisabled}
                            combinedViewMode={combinedViewMode}
                            editingCell={editingCell}
                            setEditingCell={setEditingCell}
                            editValue={editValue}
                            setEditValue={setEditValue}
                            isValidMetricInput={isValidMetricInput}
                            handleInputChange={(metricId, productName, value) =>
                                handleTotalInputChange(
                                    selectedEventInfo as PromoEvent,
                                    promoConfigDictionary,
                                    metricId,
                                    productName,
                                    value,
                                    updateSelectedEventProperty as any,
                                )
                            }
                            metricType="spendDetails"
                        />
                    )}
                    {currentPromo?.ppgs
                        ?.filter((pm: ProductPPG) => pm.isSelected)
                        ?.map((pm: ProductPPG) => {
                            return (
                                <MetricCell
                                    key={pm.id}
                                    pm={pm}
                                    headerKPIs={spendDetailKPIs}
                                    promoConfigDictionary={promoConfigDictionary}
                                    isInputDisabled={isInputDisabled}
                                    combinedViewMode={combinedViewMode}
                                    editingCell={editingCell}
                                    setEditingCell={setEditingCell}
                                    editValue={editValue}
                                    setEditValue={setEditValue}
                                    isValidMetricInput={isValidMetricInput}
                                    handleInputChange={handleInputChange}
                                    metricType="spendDetails"
                                />
                            );
                        })}
                </div>
            </div>
            <div className=" bg-backgroundGreyAlt  px-2 py-1 relative">
                <div className="inline-flex justify-start items-center gap-4 cursor-pointer">
                    <KPISelector
                        isVisible={isPopoverVisible}
                        setIsVisible={setIsPopoverVisible}
                        kpiList={metricKPIsForSelector.filter((item) => !item.isHidden) || []}
                        onSubmit={onMetricKPISelectHandler}
                        buttonName={'Add / Remove Spends'}
                        searchPlaceholder="Search KPI..."
                        variableName="Spend Metric"
                        disableMinimumOneSelectionCondition
                        addDisabled={checkPromoStatus(promoStatus as string)}
                    />
                </div>
            </div>
        </>
    );
};

export default SpendDetails;
